import { useState, type FormEvent } from "react"
import { ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../Dialog/Dialog"
import { Button } from "../Button/Button"
import { Input } from "../Input/Input"

export const UNTITLED_STRATEGY = "Untitled strategy"

export interface NameStrategyDialogProps {
  open: boolean
  /** Esc, the X button, and backdrop clicks all arrive as onOpenChange(false). */
  onOpenChange: (open: boolean) => void
  /** Receives the trimmed name; an empty input falls back to "Untitled strategy". */
  onSubmit: (name: string) => void | Promise<void>
  isSubmitting?: boolean
  error?: string | null
  className?: string
}

// The form is its own component so its draft state lives inside DialogContent,
// which Radix unmounts when the dialog closes — reopening starts fresh.
function NameStrategyForm({
  onSubmit,
  isSubmitting,
  error,
}: Pick<NameStrategyDialogProps, "onSubmit" | "isSubmitting" | "error">) {
  const [name, setName] = useState("")

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSubmitting) return
    void onSubmit(name.trim() || UNTITLED_STRATEGY)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <DialogHeader className="gap-1 text-left">
        <DialogTitle className="text-section text-foreground">
          Name your strategy
        </DialogTitle>
        <DialogDescription className="text-body text-muted-foreground">
          You can rename it anytime from the sidebar.
        </DialogDescription>
      </DialogHeader>
      <Input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. EURUSD trend-line pullback"
        aria-label="Strategy name"
        disabled={isSubmitting}
      />
      {error && (
        <p className="text-caption text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end pt-1">
        <Button
          type="submit"
          size="sm"
          className="gap-1.5 text-label"
          disabled={isSubmitting}
        >
          Start
          <ArrowRight size={13} strokeWidth={2} />
        </Button>
      </div>
    </form>
  )
}

/**
 * "Name your strategy" popup shown when creating a new strategy. The strategy
 * is only created after the user confirms the name — dismissing aborts.
 */
export function NameStrategyDialog({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  error = null,
  className,
}: NameStrategyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("v1 bg-card w-[94vw] max-w-[420px] p-6", className)}
      >
        <NameStrategyForm
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          error={error}
        />
      </DialogContent>
    </Dialog>
  )
}
